import React, { createContext, useContext, useState, useEffect } from 'react';

export type UIScale = 'small' | 'normal' | 'large';

interface UIScaleContextType {
  scale: UIScale;
  setScale: (scale: UIScale) => void;
  scaleMultiplier: number;
}

const UIScaleContext = createContext<UIScaleContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useUIScale = () => {
  const context = useContext(UIScaleContext);
  if (context === undefined) {
    throw new Error('useUIScale must be used within a UIScaleProvider');
  }
  return context;
};

interface UIScaleProviderProps {
  children: React.ReactNode;
}

export const UIScaleProvider: React.FC<UIScaleProviderProps> = ({ children }) => {
  const [scale, setScaleState] = useState<UIScale>('normal');

  // Load saved scale preference on mount
  useEffect(() => {
    const savedScale = localStorage.getItem('ui-scale') as UIScale;
    if (savedScale && ['small', 'normal', 'large'].includes(savedScale)) {
      setScaleState(savedScale);
      applyScaleToDocument(savedScale);
    }
  }, []);

  const applyScaleToDocument = (newScale: UIScale) => {
    const root = document.documentElement;

    // Scale multipliers for different sizes
    const scaleMap = {
      small: 0.875, // 87.5% - smaller for more content
      normal: 1,    // 100% - default
      large: 1.25   // 125% - larger for better visibility
    };

    const multiplier = scaleMap[newScale];

    // Update CSS custom properties
    root.style.setProperty('--ui-scale', multiplier.toString());
    root.style.setProperty('--font-size', `${14 * multiplier}px`);

    // Update base font sizes for better scaling
    root.style.setProperty('--text-xs', `${12 * multiplier}px`);
    root.style.setProperty('--text-sm', `${14 * multiplier}px`);
    root.style.setProperty('--text-base', `${16 * multiplier}px`);
    root.style.setProperty('--text-lg', `${18 * multiplier}px`);
    root.style.setProperty('--text-xl', `${20 * multiplier}px`);
    root.style.setProperty('--text-2xl', `${24 * multiplier}px`);

    // Update spacing scale
    root.style.setProperty('--spacing-scale', multiplier.toString());

    // Add scale class for conditional styling
    root.classList.remove('ui-scale-small', 'ui-scale-normal', 'ui-scale-large');
    root.classList.add(`ui-scale-${newScale}`);
  };

  const setScale = (newScale: UIScale) => {
    setScaleState(newScale);
    localStorage.setItem('ui-scale', newScale);
    applyScaleToDocument(newScale);
  };

  const getScaleMultiplier = (scale: UIScale): number => {
    const scaleMap = {
      small: 0.875,
      normal: 1,
      large: 1.25
    };
    return scaleMap[scale];
  };

  const value: UIScaleContextType = {
    scale,
    setScale,
    scaleMultiplier: getScaleMultiplier(scale)
  };

  return (
    <UIScaleContext.Provider value={value}>
      {children}
    </UIScaleContext.Provider>
  );
};